import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TaskDefinitionSummary, DocumentContent, isStreamingTaskType, getNoteTemplateFromTaskType, Account, isTrialExpired, hasActiveSubscription } from '../../types';
import { TemplateDropdown } from '../TemplateDropdown';
import { PatientInput } from '../PatientInput';
import { RecordAudioSection } from '../RecordAudioSection';
import { UploadDocuments } from '../UploadDocuments';
import { ThumbnailList } from '../ThumbnailList';
import { GenerateButton } from '../GenerateButton';
import { ScreenLockInstructionsModal } from '../ScreenLockInstructionsModal';
import { ArrowLeft, Upload, FileText, User, Crown, AlertTriangle } from 'lucide-react';

interface StepBasedUploaderProps {
  templates: TaskDefinitionSummary[];
  selectedTemplate: string;
  onTemplateChange: (template: string) => void;
  onTemplateSettingsClick: () => void;

  onAddTemplateClick?: () => void;
  onFilesAdded: (files: DocumentContent[]) => void;
  attachments: DocumentContent[];
  onRemoveAttachment: (index: number) => void;
  onGenerate: () => void;
  isGenerateDisabled: boolean;
  isLoading?: boolean;
  selectedPatient: string;
  onPatientChange: (patientId: string) => void;
  onShowAddModal: () => void;
  scrollToBottom?: () => void;

  getPatientNameById?: (patientId: string) => string;
  shouldResetToStep1?: boolean;
  refreshPatientsTrigger?: number;
  account?: Account; // Account for HealthScribe
  onRecordingComplete?: (userRequestId: string) => void;
}

type Step = 1 | 2 | 3;

export const StepBasedUploader: React.FC<StepBasedUploaderProps> = ({
  templates,
  selectedTemplate,
  onTemplateChange,
  onTemplateSettingsClick,
  onAddTemplateClick,
  onFilesAdded,
  attachments,
  onRemoveAttachment,
  onGenerate,
  isGenerateDisabled,
  isLoading,
  selectedPatient,
  onPatientChange,
  onShowAddModal,
  scrollToBottom,
  getPatientNameById,
  shouldResetToStep1,
  refreshPatientsTrigger,
  account,
  onRecordingComplete,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [showScreenLockModal, setShowScreenLockModal] = useState(false);
  const [isRecordingLocked, setIsRecordingLocked] = useState(false);
  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  
  
  // Safety check: ensure we have valid template data before proceeding
  const isValidTemplate = selectedTemplateData && selectedTemplateData.taskType;

  console.log('isValidTemplate', isValidTemplate);
  
  // Debug logging to help track template selection issues
  React.useEffect(() => {
    if (selectedTemplate && !isValidTemplate) {
      console.warn('Template selection issue:', {
        selectedTemplate,
        templatesCount: templates.length,
        selectedTemplateData,
        templates: templates.map(t => ({ id: t.id, name: t.name, taskType: t.taskType }))
      });
    }
  }, [selectedTemplate, isValidTemplate, templates]);
  
  // Ensure we have a valid template when templates are available
  React.useEffect(() => {
    if (templates.length > 0 && !isValidTemplate && onTemplateChange) {
      // Auto-select the first available template if none is selected
      const firstTemplate = templates[0];
      if (firstTemplate && firstTemplate.taskType) {
        onTemplateChange(firstTemplate.id);
      }
    }
  }, [templates, isValidTemplate, onTemplateChange]);

  // Reset to step 1 when parent requests it
  React.useEffect(() => {
    if (shouldResetToStep1) {
      setCurrentStep(1);
    }
  }, [shouldResetToStep1]);

  const handleStep1Complete = () => {
    if (isValidTemplate) {
      setCurrentStep(2);
    }
  };

  const handleStep2Complete = () => {
    if (attachments.length > 0) {
      setCurrentStep(3);
    }
  };

  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  const handleGenerate = () => {
    onGenerate();
  };

  const canAdvanceFromStep1 = isValidTemplate;
  const canAdvanceFromStep2 = attachments.length > 0;
  
  // Determine if this is a streaming note template workflow (2 steps) or regular workflow (3 steps)
  const isStreaming = isValidTemplate && isStreamingTaskType(selectedTemplateData?.taskType || '');
  
  // Check trial status
  const isTrialExpiredForAccount = account && isTrialExpired(account) && !hasActiveSubscription(account);

  const steps = isStreaming ? [
    { id: 1, title: 'Note Type & Patient' },
    { id: 2, title: 'Record Audio' }
  ] : [
    { id: 1, title: 'Note Type & Patient' },
    { id: 2, title: 'Upload Documents' },
    { id: 3, title: 'Review & Generate' }
  ];

  return (
    <div className="container mx-auto p-3 md:p-6 max-w-4xl mt-3 md:mt-8 animate-in fade-in duration-200">
      {/* Progress Indicator */}
      <div className="mb-3 md:mb-8">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`flex-1 ${index > 0 ? 'ml-2' : ''}`}
            >
              <div className={`
                text-xs font-medium mb-2 transition-colors duration-200
                ${currentStep === step.id ? 'text-primary-content' : 'text-base-content/50'}
              `}>
                {step.id}: {step.title}
              </div>
              <div className={`
                h-1 rounded-full transition-all duration-300
                ${currentStep === step.id 
                  ? 'bg-primary' 
                  : 'bg-base-300'
                }
              `} />
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Note Type & Patient */}
      {currentStep === 1 && (
        <div className="bg-base-100 p-4 md:p-6 rounded-lg border border-base-300">
          {/* Temporarily disabled - Section header */}
          {/* <div className="text-center mb-6">
            <h2 className="card-title text-2xl justify-center text-base-content mb-3">Note Type & Patient</h2>
            <p className="text-base text-base-content/70 max-w-lg mx-auto">Choose a template and enter patient information to get started</p>
          </div> */}

          <div className="space-y-4 md:space-y-6">
            {/* Template Selection */}
            <div className="form-control">
              <label className="label pb-2">
                <span className="label-text text-base font-medium text-base-content">Note Type</span>
              </label>
              <div className="mt-1">
                <TemplateDropdown
                  templates={templates}
                  selected={selectedTemplate}
                  onChange={onTemplateChange}
                  onSettingsClick={onTemplateSettingsClick}
                  onAddTemplateClick={onAddTemplateClick}
                />
              </div>
            </div>

            {/* Patient Information */}
            <div className="form-control">
              <label className="label pb-2">
                <span className="label-text text-base font-medium text-base-content">Patient Information</span>
              </label>
              <div className="mt-1">
                <PatientInput
                  selectedPatient={selectedPatient}
                  onPatientChange={onPatientChange}
                  onShowAddModal={onShowAddModal}
                  refreshTrigger={refreshPatientsTrigger}
                />
              </div>
            </div>

            {/* Next Button */}
            <div className="pt-4">
              {isTrialExpiredForAccount ? (
                <>
                  <Link to="/pricing" className="btn btn-primary w-full">
                    <Crown className="w-4 h-4" />
                    Upgrade to Pro
                  </Link>
                  <div className="mt-3 text-center">
                    <p className="text-sm text-base-content/70">
                      Your free trial has ended. Upgrade to continue creating new notes.
                    </p>
                  </div>
                </>
              ) : (
                <button
                  onClick={handleStep1Complete}
                  disabled={!canAdvanceFromStep1}
                  className="btn btn-primary w-full"
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Upload Documents */}
      {currentStep === 2 && isValidTemplate && (
        <div className="bg-base-100 p-4 md:p-6 rounded-lg border border-base-300">
          <div className="space-y-4 md:space-y-6">
            {/* Conditional Upload Section based on taskType */}
            {isStreaming ? (
              <>
                <div className="bg-base-200 p-4 rounded-lg border border-base-300">
                  {isTrialExpiredForAccount ? (
                    <div className="text-center py-8">
                      <div className="text-lg font-semibold text-base-content mb-2">Trial Expired</div>
                      <div className="text-base-content/70 mb-4">
                        Your free trial has expired. Upgrade to Pro to continue recording new notes.
                      </div>
                      <div className="text-sm text-info">
                        You can still view your existing patient notes.
                      </div>
                    </div>
                  ) : (
                    <RecordAudioSection
                      onFilesAdded={onFilesAdded}
                      patientId={selectedPatient}
                      account={account}
                      noteTemplate={getNoteTemplateFromTaskType(selectedTemplateData?.taskType || '')}
                      taskDefinitionId={selectedTemplate}
                      onRecordingComplete={onRecordingComplete}
                      onRecordingStatusChange={setIsRecordingLocked}
                    />
                  )}
                </div>
                
                {/* Screen Lock Warning */}
                {!isTrialExpiredForAccount && (
                  <div className="flex items-start gap-2 p-2.5 bg-warning/10 border border-warning/20 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                    <span className="text-sm text-base-content/70 leading-relaxed">
                      Keep your screen <b>awake</b> during recording to maintain connection.{' '}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowScreenLockModal(true);
                        }}
                        className="link link-primary link-hover underline font-medium text-base-content/90"
                      >
                        Learn how to configure
                      </button>
                    </span>
                  </div>
                )}
              </>
            ) : isValidTemplate && (selectedTemplateData?.taskType === 'DocumentPopulation' || selectedTemplateData?.taskType === 'CMS1500_SOAP') ? (
              isTrialExpiredForAccount ? (
                <div className="bg-base-200 p-4 rounded-lg border border-base-300 text-center">
                  <div className="text-lg font-semibold text-base-content mb-2">Trial Expired</div>
                  <div className="text-base-content/70 mb-4">
                    Your free trial has expired. Upgrade to Pro to continue processing documents.
                  </div>
                  <div className="text-sm text-info">
                    You can still view your existing patient notes.
                  </div>
                </div>
              ) : (
                <UploadDocuments
                  onFilesAdded={onFilesAdded}
                />
              )
            ) : (
              <div className="bg-base-200 p-4 rounded-lg border border-base-300 text-center">
                <p className="text-base-content/70">Please select a valid template to continue</p>
              </div>
            )}

            {/* Attachments List */}
            {attachments.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-base-content">Uploaded Files</h3>
                <ThumbnailList
                  attachments={attachments}
                  onRemove={onRemoveAttachment}
                  scrollToBottom={scrollToBottom}
                />
              </div>
            )}

            {/* Navigation */}
            <div className={`flex ${isStreaming && isRecordingLocked ? 'justify-end' : 'justify-between'} pt-4 items-center`}>
              {!(isStreaming && isRecordingLocked) && (
                <button
                  onClick={handleGoBack}
                  className="btn btn-outline border-[0.5px]"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
              )}
              {isStreaming ? (
                // For streaming note templates, only show Generate button if NOT a SOAP note
                selectedTemplateData?.taskType && !selectedTemplateData.taskType.toLowerCase().includes('soap') ? (
                  <GenerateButton
                    onClick={handleGenerate}
                    disabled={isGenerateDisabled || Boolean(isTrialExpiredForAccount)}
                    isLoading={isLoading}
                    taskType={selectedTemplateData?.taskType}
                  />
                ) : null
              ) : (
                // For other templates, continue to step 3
                <button
                  onClick={handleStep2Complete}
                  disabled={!canAdvanceFromStep2}
                  className="btn btn-primary"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: No Valid Template */}
      {currentStep === 2 && !isValidTemplate && (
        <div className="bg-base-100 p-6 rounded-lg border border-base-300">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="card-title text-2xl text-base-content mb-3">No Valid Template Selected</h2>
              <p className="text-base text-base-content/70">Please go back and select a valid template to continue</p>
            </div>
            <button
              onClick={handleGoBack}
              className="btn btn-outline btn-sm border-[0.5px]"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review & Generate (only for non-streaming task types) */}
      {currentStep === 3 && isValidTemplate && !isStreaming && (
        <div className="bg-base-100 p-4 md:p-6 rounded-lg border border-base-300">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div>
              <h2 className="card-title text-xl md:text-2xl text-base-content mb-2 md:mb-3">Review & Generate</h2>
              <p className="text-sm md:text-base text-base-content/70">Review your selections and generate documents</p>
            </div>
            <button
              onClick={handleGoBack}
              className="btn btn-outline btn-sm border-[0.5px]"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          </div>

          <div className="space-y-4 md:space-y-6">
            {/* Summary */}
            <div className="bg-base-200 p-4 rounded-lg border border-base-300">
              <h3 className="card-title text-lg font-semibold text-base-content mb-4">Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-base-content/50" />
                  <div>
                    <div className="text-sm font-semibold text-base-content">Template</div>
                    <div className="text-sm text-base-content/70">{selectedTemplateData?.name || 'Selected template'}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-base-content/50" />
                  <div>
                    <div className="text-sm font-semibold text-base-content">Patient</div>
                    <div className="text-sm text-base-content/70">
                      {selectedPatient 
                        ? (getPatientNameById ? getPatientNameById(selectedPatient) : selectedPatient)
                        : 'Not specified'}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Upload className="w-5 h-5 text-base-content/50" />
                  <div>
                    <div className="text-sm font-semibold text-base-content">Files</div>
                    <div className="text-sm text-base-content/70">{attachments.length} file(s) uploaded</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments Review */}
            {attachments.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-base-content">Uploaded Files</h3>
                <ThumbnailList
                  attachments={attachments}
                  onRemove={onRemoveAttachment}
                  scrollToBottom={scrollToBottom}
                />
              </div>
            )}

            {/* Generate Button */}
            <div className="flex justify-between pt-4">
              <button
                onClick={handleGoBack}
                className="btn btn-outline border-[0.5px]"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <GenerateButton
                onClick={handleGenerate}
                disabled={isGenerateDisabled || Boolean(isTrialExpiredForAccount)}
                isLoading={isLoading}
                taskType={isValidTemplate ? selectedTemplateData?.taskType : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Screen Lock Instructions Modal */}
      <ScreenLockInstructionsModal
        isOpen={showScreenLockModal}
        onClose={() => setShowScreenLockModal(false)}
      />
    </div>
  );
}; 